import React from 'react';
import {
  Paper,
  Grid,
  makeStyles,
  Button,
  CircularProgress,
  colors,
} from '@material-ui/core';
import { Home, Send } from '@material-ui/icons';
import { Link } from 'react-router-dom';

import Routes from 'config/routes';

import Title from 'components/typography/Title';
import MainTextfield from 'components/textfield/MainTextfield';
import PasswordTextfield from 'components/textfield/PasswordTextfield';
import Description from 'components/typography/Description';

import SendVerificationEmailButton from 'components/buttons/SendVerificationEmailButton';
import VerifiedButton from 'components/buttons/VerifiedButton';

const useStyles = makeStyles({
  root: {
    padding: 16,
    marginTop: 64,
    width: '100%',
  },
  title: {
    color: colors.red[400],
  },
  button: {
    color: colors.grey[50],
    backgroundColor: colors.red[400],
    '&:hover': {
      backgroundColor: colors.red[100],
    },
  },
  secondaryButton: {
    color: colors.red[400],
    '&:hover': {
      color: colors.grey[50],
      backgroundColor: colors.red[100],
    },
  },
});

const LoginCard = ({
  loginForm,
  setLoginForm,
  onSubmitButtonPressed,
  loginErrors,
  isSubmiting,
  hasFinished,
  hasVerifiedEmail,
  exitPage,
}) => {
  const classes = useStyles();

  return (
    <Paper elevation={4} className={classes.root}>
      <Grid container direction="column" alignItems="center" spacing={1}>
        <Grid item container justify="center" style={{ paddingBottom: 32 }}>
          <Title>
            <span className={classes.title}>Login</span>
          </Title>
        </Grid>

        <Grid item container>
          {hasFinished ? (
            <Finished
              hasVerifiedEmail={hasVerifiedEmail}
              exitPage={exitPage}
              loginForm={loginForm}
            ></Finished>
          ) : (
            <Form
              isSubmiting={isSubmiting}
              loginErrors={loginErrors}
              loginForm={loginForm}
              onSubmitButtonPressed={onSubmitButtonPressed}
              setLoginForm={setLoginForm}
            ></Form>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

const Finished = ({ hasVerifiedEmail, exitPage, loginForm }) => {
  const classes = useStyles();

  return (
    <Grid container direction="column" spacing={4}>
      <Grid item container justify="center">
        <Description>
          {hasVerifiedEmail ? (
            <span>
              Login was <b>successful</b>
            </span>
          ) : (
            <span>
              You did not <b>verify</b> this email
            </span>
          )}
        </Description>
      </Grid>

      {hasVerifiedEmail ? (
        <Grid item container spacing={1} direction="column">
          <Grid item container justify="center">
            <Link to={Routes.HOME_PAGE} style={{ textDecoration: 'none' }}>
              <Button className={classes.button} endIcon={<Home></Home>}>
                GO TO HOME
              </Button>
            </Link>
          </Grid>

          <Grid item container justify="center">
            <Button className={classes.secondaryButton} onClick={exitPage}>
              GO BACK
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid item container direction="column" spacing={1}>
          <Grid item container justify="center">
            <SendVerificationEmailButton></SendVerificationEmailButton>
          </Grid>

          <Grid item container justify="center">
            <VerifiedButton
              isContained={false}
              email={loginForm.email}
              password={loginForm.password}
              onVerified={exitPage}
            ></VerifiedButton>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const Form = ({
  loginForm,
  loginErrors,
  isSubmiting,
  onSubmitButtonPressed,
  setLoginForm,
}) => {
  const classes = useStyles();

  const onChange = (key) => (value) =>
    setLoginForm({ ...loginForm, [key]: value });

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item container style={{ marginBottom: 8 }}>
        <MainTextfield
          fullWidth={true}
          label="Email"
          value={loginForm.email}
          onChange={(e) => onChange('email')(e.target.value)}
          error={!!loginErrors.email}
          helperText={loginErrors.email}
        ></MainTextfield>
      </Grid>

      <Grid item container>
        <PasswordTextfield
          value={loginForm.password}
          onChange={onChange('password')}
          errorMessage={loginErrors.password}
        ></PasswordTextfield>
      </Grid>

      <Grid
        item
        container
        style={{ paddingTop: 32 }}
        justify="flex-end"
        alignItems="center"
      >
        <Grid item>
          <Button
            variant="contained"
            onClick={onSubmitButtonPressed}
            className={classes.button}
            endIcon={
              isSubmiting ? (
                <CircularProgress
                  size={20}
                  style={{ color: 'inherit' }}
                ></CircularProgress>
              ) : (
                <Send></Send>
              )
            }
          >
            SUBMIT
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default LoginCard;
